import { useEffect, useState } from 'react';
import { X, Printer, Download, Mail, Copy, PenLine } from 'lucide-react';
import axios from 'axios';
import api, { documentsApi } from '../utils/api';
import InvoiceTemplate from './InvoiceTemplate';
import CorregirDescripcionModal from './CorregirDescripcionModal';
import toast from 'react-hot-toast';
import useEscClose from '../hooks/useEscClose';

export default function DocumentDetailModal({ documentId, emisor, onClose }) {
  useEscClose(onClose);

  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipoCambio, setTipoCambio] = useState(1);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Corrección de descripción: permiso concedido a mano. Quien no lo tenga
  // no ve el botón siquiera.
  const [puedeCorregir, setPuedeCorregir] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState(false);

  useEffect(() => {
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  useEffect(() => {
    let vivo = true;
    documentsApi.puedeCorregirDescripcion()
      .then(r => { if (vivo) setPuedeCorregir(!!r.data?.data?.permitido); })
      .catch(() => { if (vivo) setPuedeCorregir(false); });
    return () => { vivo = false; };
  }, []);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const response = await documentsApi.getById(documentId);
      const doc = response.data.data;
      setDocumento(doc);

      // Si es USD, obtener tipo de cambio
      const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
      const docTipoCambio = parseFloat(doc.tipoCambio || doc.tipo_cambio) || 1;

      if (moneda === 'USD') {
        if (docTipoCambio > 1) {
          setTipoCambio(docTipoCambio);
        } else {
          // Tabla tipos_cambio del tenant — ver project_finance360_tipo_cambio.
          try {
            const hoyCR = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Costa_Rica' });
            const tcResponse = await api.get(`/exchange-rate/${hoyCR}`);
            if (tcResponse.data?.success && tcResponse.data?.venta) {
              setTipoCambio(tcResponse.data.venta);
            } else {
              setTipoCambio(497);
            }
          } catch (tcError) {
            setTipoCambio(497);
          }
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadXml = async () => {
    if (!documento) return;

    try {
      const response = await documentsApi.downloadXml(documentId);
      const contentType = response.headers['content-type'] || '';
      const isZip = contentType.includes('application/zip');
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isZip
        ? `${documento.consecutivo}.zip`
        : `${documento.consecutivo}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      if (isZip) {
        toast.success('XMLs descargados');
      } else {
        toast.success('XML descargado');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al descargar XML');
    }
  };

  const handleSendEmail = async () => {
    if (!documento || documento.status !== 'accepted') {
      toast.error('Solo se pueden enviar por email documentos aceptados');
      return;
    }

    setSendingEmail(true);
    try {
      await documentsApi.sendEmail(documentId);
      toast.success('Email enviado al cliente');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopyKey = () => {
    if (documento?.clave) {
      navigator.clipboard.writeText(documento.clave);
      toast.success('Clave copiada al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando documento...</p>
        </div>
      </div>
    );
  }

  if (!documento) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <p className="text-red-600">Error al cargar el documento</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded">Cerrar</button>
        </div>
      </div>
    );
  }

  // Prepare data for InvoiceTemplate
  const lineas = documento.lineas || [];
  const moneda = documento.codigo_moneda || documento.codigoMoneda || 'CRC';

  const receptor = {
    nombre: documento.receptorNombre || documento.receptor_nombre,
    identificacion: documento.receptorIdentificacion || documento.receptor_identificacion,
    tipo_identificacion: documento.receptorTipoIdentificacion || documento.receptor_tipo_identificacion,
    email: documento.receptorEmail || documento.receptor_email,
    email_facturacion: documento.receptorEmailFacturacion || documento.receptor_email_facturacion,
    telefono_codigo: documento.receptorTelefonoCodigo || documento.receptor_telefono_codigo || '506',
    telefono_numero: documento.receptorTelefonoNumero || documento.receptor_telefono_numero,
    otras_senas: documento.receptorOtrasSenas || documento.receptor_otras_senas,
    provincia: documento.receptorProvincia || documento.receptor_provincia,
    canton: documento.receptorCanton || documento.receptor_canton,
    distrito: documento.receptorDistrito || documento.receptor_distrito,
    dias_credito: documento.receptorDiasCredito || documento.receptor_dias_credito || documento.diasCredito || 30
  };

  // Calculate totals from line items if not present in document
  const calcularTotales = () => {
    let totalGravado = 0;
    let totalExento = 0;
    let totalDescuentos = 0;
    let totalImpuesto = 0;
    let totalVenta = 0;

    // Para desglose de impuestos
    let totalIVA = 0;
    const impuestosAdicionalesMap = {};

    lineas.forEach(l => {
      const subtotal = parseFloat(l.subtotal || l.monto_total || (l.cantidad * l.precio_unitario) || 0);
      const descuento = parseFloat(l.descuento_monto || l.descuentoMonto || 0);
      const impuesto = parseFloat(l.impuesto_monto || l.impuestoMonto || 0);
      const total = parseFloat(l.monto_total_linea || l.montoTotalLinea || 0);

      if (impuesto > 0) {
        totalGravado += subtotal - descuento;
      } else {
        totalExento += subtotal - descuento;
      }
      totalDescuentos += descuento;
      totalImpuesto += impuesto;
      totalIVA += impuesto;
      totalVenta += total;

      // Sumar impuestos adicionales de cada línea
      const impuestosAd = l.impuestos_adicionales || l.impuestosAdicionales || [];
      impuestosAd.forEach(imp => {
        const codigo = imp.impuesto_codigo || imp.codigo || imp.codigoImpuesto;
        const monto = parseFloat(imp.impuesto_monto || imp.monto || 0);
        const descripcion = imp.descripcion || (codigo === '07' ? 'Impuesto 911' : codigo === '99' ? 'Cruz Roja' : `Impuesto ${codigo}`);

        if (!impuestosAdicionalesMap[codigo]) {
          impuestosAdicionalesMap[codigo] = { descripcion, monto: 0 };
        }
        impuestosAdicionalesMap[codigo].monto += monto;
        totalImpuesto += monto;
      });
    });

    // Crear array de desglose
    const impuestosDesglose = [];
    if (totalIVA > 0) {
      impuestosDesglose.push({ codigo: '01', descripcion: 'IVA', monto: totalIVA });
    }
    Object.entries(impuestosAdicionalesMap).forEach(([codigo, imp]) => {
      if (imp.monto > 0) {
        impuestosDesglose.push({ codigo, ...imp });
      }
    });

    // Calcular totales correctamente incluyendo impuestos adicionales
    const totalVentaNetaCalc = (documento.total_gravado || totalGravado) + (documento.total_exento || totalExento) - (documento.total_descuentos || totalDescuentos);
    const totalComprobanteCalc = totalVentaNetaCalc + totalImpuesto;

    return {
      totalGravado: documento.total_gravado || totalGravado,
      totalExento: documento.total_exento || totalExento,
      totalDescuentos: documento.total_descuentos || totalDescuentos,
      totalImpuesto: totalImpuesto, // Siempre usar el calculado (incluye IVA + adicionales)
      totalVenta: totalComprobanteCalc, // Usar el calculado
      totalVentaNeta: totalVentaNetaCalc,
      totalComprobante: totalComprobanteCalc, // Usar el calculado (incluye todos los impuestos)
      totalOtrosCargos: documento.total_otros_cargos || 0,
      impuestosDesglose: impuestosDesglose.length > 0 ? impuestosDesglose : undefined
    };
  };

  const totales = calcularTotales();

  const documentoForTemplate = {
    consecutivo: documento.consecutivo,
    clave: documento.clave,
    tipoDocumento: documento.tipo_documento || documento.tipoDocumento,
    fechaEmision: documento.fecha_emision || documento.fechaEmision,
    condicionVenta: documento.condicion_venta || documento.condicionVenta,
    estadoPago: documento.estado_pago || documento.estadoPago,
    periodoCobroTexto: documento.periodo_cobro_texto || documento.periodoCobroTexto,
    medioPago: documento.medio_pago || documento.medioPago,
    codigoMoneda: moneda,
    status: documento.status,
    notas: documento.notas,
    diasCredito: documento.plazo_credito || documento.plazoCredito || receptor.dias_credito,
    ordenCompra: documento.metadata?.ordenCompra || '',
    referencia: documento.referencia ? {
      tipoDoc: documento.referencia.tipoDoc || documento.referencia.tipo_doc || documento.referencia_tipo_doc,
      numero: documento.referencia.numero || documento.referencia_numero,
      consecutivo: documento.referencia.consecutivo || documento.referencia_consecutivo,
      fecha: documento.referencia.fecha || documento.referencia_fecha,
      codigo: documento.referencia.codigo || documento.referencia_codigo,
      razon: documento.referencia.razon || documento.referencia_razon
    } : (documento.referencia_numero ? {
      tipoDoc: documento.referencia_tipo_doc,
      numero: documento.referencia_numero,
      consecutivo: documento.referencia_consecutivo,
      fecha: documento.referencia_fecha,
      codigo: documento.referencia_codigo,
      razon: documento.referencia_razon
    } : null)
  };

  // Format lineas for template
  const lineasForTemplate = lineas.map((l, idx) => {
    // Parse impuestos adicionales
    const impuestosAdicionales = (l.impuestos_adicionales || l.impuestosAdicionales || []).map(imp => ({
      codigoImpuesto: imp.impuesto_codigo || imp.codigo || imp.codigoImpuesto,
      porcentaje: imp.porcentaje || imp.tarifa || 0,
      monto: parseFloat(imp.impuesto_monto || imp.monto || 0),
      descripcion: imp.descripcion || ''
    }));

    return {
      numeroLinea: l.numero_linea || l.numeroLinea || idx + 1,
      codigo: l.codigo || l.Codigo,
      codigoComercial: l.codigo_comercial || l.codigoComercial,
      sku: l.sku || l.codigo_comercial || l.codigoComercial,
      detalle: l.detalle || l.Detalle,
      cantidad: l.cantidad || l.Cantidad || 1,
      unidadMedida: l.unidad_medida || l.unidadMedida || 'Unid',
      precioUnitario: l.precio_unitario || l.precioUnitario || l.PrecioUnitario || 0,
      montoTotal: l.monto_total || l.montoTotal || 0,
      descuentoMonto: l.descuento_monto || l.descuentoMonto || 0,
      subtotal: l.subtotal || l.Subtotal || 0,
      impuestoTarifa: l.impuesto_tarifa || l.impuestoTarifa || 0,
      impuestoMonto: l.impuesto_monto || l.impuestoMonto || 0,
      impuestoExoneracion: l.impuesto_exoneracion || l.impuestoExoneracion,
      impuestosAdicionales: impuestosAdicionales,
      montoTotalLinea: l.monto_total_linea || l.montoTotalLinea || l.MontoTotalLinea || 0
    };
  });

  // Prepare emisor for template
  const emisorForTemplate = {
    nombre: emisor?.nombre || emisor?.name,
    nombre_comercial: emisor?.nombre_comercial || emisor?.nombreComercial,
    identificacion: emisor?.identificacion,
    tipo_identificacion: emisor?.tipo_identificacion || emisor?.tipoIdentificacion || '02',
    email: emisor?.email,
    telefono_codigo: emisor?.telefono_codigo || '506',
    telefono_numero: emisor?.telefono_numero || emisor?.telefonoNumero,
    provincia: emisor?.provincia,
    canton: emisor?.canton,
    distrito: emisor?.distrito,
    otras_senas: emisor?.otras_senas || emisor?.otrasSenas,
    logo_url: emisor?.logo_url || emisor?.logoUrl,
    template_color: emisor?.template_color || emisor?.templateColor || '#F47920',
    terminos_condiciones: emisor?.terminos_condiciones || emisor?.terminosCondiciones
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Detalle del Documento
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-sm text-gray-600">{documento.consecutivo}</span>
              {documento.clave && (
                <button
                  onClick={handleCopyKey}
                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 rounded"
                  title="Copiar clave numérica"
                >
                  <Copy className="h-3 w-3" />
                  Clave
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {puedeCorregir && (
              <button
                onClick={() => setCorrigiendo(true)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-amber-400 text-amber-700 rounded-lg hover:bg-amber-50"
                title="Corregir la descripción de una línea (queda en bitácora)"
              >
                <PenLine className="h-4 w-4" />
                Corregir
              </button>
            )}
            <button
              onClick={handleSendEmail}
              disabled={sendingEmail || documento.status !== 'accepted'}
              className="inline-flex items-center gap-2 px-3 py-2 border border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={documento.status !== 'accepted' ? 'Solo documentos aceptados' : 'Enviar por email'}
            >
              <Mail className="h-4 w-4" />
              {sendingEmail ? 'Enviando...' : 'Email'}
            </button>
            <button
              onClick={handleDownloadXml}
              className="inline-flex items-center gap-2 px-3 py-2 border border-teal-400 text-teal-600 rounded-lg hover:bg-teal-50"
            >
              <Download className="h-4 w-4" />
              XML
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
            >
              <Printer className="h-4 w-4" />
              Imprimir
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
          <div className="mx-auto shadow-lg" style={{ maxWidth: '210mm' }}>
            <InvoiceTemplate
              documento={documentoForTemplate}
              emisor={emisorForTemplate}
              receptor={receptor}
              lineas={lineasForTemplate}
              totales={totales}
              esCotizacion={false}
              tipoCambio={tipoCambio}
            />
          </div>
        </div>
      </div>

      {corrigiendo && (
        <CorregirDescripcionModal
          documento={documento}
          lineas={lineas}
          onClose={() => setCorrigiendo(false)}
          onSaved={() => { setCorrigiendo(false); loadDocument(); }}
        />
      )}
    </div>
  );
}
